import * as XLSX from 'xlsx'
import {saveAs} from 'file-saver'

export type ExportToExcelArguments = {
    data: any[]
    fileName: string
    /** Column order for the header row; defaults to the keys of the first row. */
    columns?: string[]
}

/** Objects and arrays (jsonb columns) would otherwise land in the file as blanks. */
const cellValue = (value: unknown): unknown =>
    value !== null && typeof value === 'object' && !(value instanceof Date) ? JSON.stringify(value) : value

const headerOf = (data: any[], columns?: string[]): string[] =>
    columns ?? Object.keys(data[0] ?? {})

export const exportToExcel = ({data, fileName, columns}: ExportToExcelArguments) => {
    const header = headerOf(data, columns)
    if (!header.length) return

    const rows = data.map(row => Object.fromEntries(header.map(key => [key, cellValue(row[key])])))
    const worksheet = XLSX.utils.json_to_sheet(rows, {header})
    const workbook = XLSX.utils.book_new()

    XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: "A1" });
    XLSX.utils.book_append_sheet(workbook, worksheet, fileName)

    const workbookOut = XLSX.write(workbook, {bookType: 'xlsx', bookSST: true, type: 'binary'})

    saveAs(new Blob([s2ab(workbookOut)],{type:'application/octet-stream'}), `${fileName}.xlsx`)
}

/**
 * A text cell a spreadsheet would run as a formula when the CSV is opened
 * (=, +, -, @, tab, CR) — pure numbers such as "-12.50" are left alone.
 */
const FORMULA_PREFIX = /^[=+\-@\t\r]/
const NUMERIC = /^-?\d+(\.\d+)?$/

export const csvSafe = (value: unknown): unknown =>
  typeof value === 'string' && FORMULA_PREFIX.test(value) && !NUMERIC.test(value) ? `'${value}` : value

export type ExportToCsvArguments = ExportToExcelArguments

export const exportToCsv = ({data, fileName, columns}: ExportToCsvArguments) => {
    const header = headerOf(data, columns)
    if (!header.length) return

    const safe = data.map(row => Object.fromEntries(header.map(key => [key, csvSafe(cellValue(row[key]))])))
    const worksheet = XLSX.utils.json_to_sheet(safe, {header})
    const csv = XLSX.utils.sheet_to_csv(worksheet)

    // The BOM makes Excel read the file as UTF-8 (names with accents stay intact).
    saveAs(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}), `${fileName}.csv`)
}

const s2ab = (s: any ) => {
    const buf = new ArrayBuffer(s.length)
    const view = new Uint8Array(buf)
    for (let i=0; i!==s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF
    return buf
}

